import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import ParticleField from "@/components/ParticleField";
import { useContent } from "@/context/ContentContext";

const PILLARS = [
  { t: "Imagine", lines: ["Challenge what already exists.", "Question limitations.", "See opportunities others overlook."] },
  { t: "Build", lines: ["Turn ideas into products.", "Turn problems into systems.", "Turn imagination into usable technology."] },
  { t: "Evolve", lines: ["Learn from users.", "Learn from data.", "Continuously improve the technology."] },
];

export default function Mission() {
  const { content } = useContent();
  const body = (content.mission_body || "").split("\n\n");

  return (
    <div>
      <SEO title="Mission — Turn Imagination Into Infrastructure | ImJove Tech" description="ImJove Tech exists to turn forward-thinking ideas into usable technology that removes friction and unlocks opportunity." />
      <section className="relative overflow-hidden pb-20 pt-40">
        <ParticleField className="absolute inset-0 z-0" density={40} />
        <div className="radial-fade absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">Our Mission</p>
            <h1 className="mt-4 font-display text-5xl font-bold leading-[1] tracking-tighter text-white sm:text-7xl">
              Turn imagination into <span className="text-gold-gradient">infrastructure.</span>
            </h1>
            <div className="mt-10 max-w-2xl space-y-5">
              {body.map((para, i) => (
                <p key={i} className="font-body text-lg leading-relaxed text-white/60">{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-ink-800 py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">Company Philosophy</p></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.1}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-ink-900 p-8">
                  <span className="font-mono2 text-xs text-white/30">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-4xl font-bold tracking-tighter text-gold-gradient">{p.t}</h3>
                  <ul className="mt-6 space-y-2.5">
                    {p.lines.map((l) => <li key={l} className="font-body text-white/60">{l}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 text-center font-display text-2xl font-light uppercase tracking-brand text-white sm:text-3xl">Imagine. Build. Evolve.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-[1100px] px-6 text-center md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl font-bold tracking-tighter text-white sm:text-5xl">Build the future with us.</h2>
            <Link to="/contact" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-mono2 text-[12px] font-semibold uppercase tracking-widest">
              Start a Conversation <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
